import React, { useCallback } from 'react';
import TableTop from '../../../core/table/TableTop';
import TableList from '../../../core/table/TableList';
import TableBody from '../../../core/table/TableBody';
import TablePaging from '../../../core/table/TablePaging';
import TableHead from '../../../core/table/TableHead';
import { useRouter } from '@uirouter/react';
import { useInvoiceLineItemDispensesTableState } from './useInvoiceLineItemDispensesTableState';
import { useInvoiceLineItemDispensesTableInit } from './useInvoiceLineItemDispensesTableInit';
import { useInvoiceLineItemDispensesData } from './useInvoiceLineItemDispensesData';
import { InvoiceLineItemDispensesDataItem } from './invoice_line_item_dispenses_list_types';

const invoiceLineItemDispensesTableId = 'invoiceLineItemDispensesTableList';

type InvoiceLineItemDispensesTableListProps = {
    invoiceId: string;
    lineItemId: string;
    rebatesOnly: boolean;
    userType: string;
};

const InvoiceLineItemDispensesTableList = ({
    invoiceId,
    lineItemId,
    rebatesOnly,
    userType,
}: InvoiceLineItemDispensesTableListProps): JSX.Element => {
    const { init } = useInvoiceLineItemDispensesTableInit();
    if (init) {
        return <></>;
    }
    return (
        <TableWrapper
            invoiceId={invoiceId}
            lineItemId={lineItemId}
            rebatesOnly={rebatesOnly}
            userType={userType}
        />
    );
};

const TableWrapper = ({
    invoiceId,
    lineItemId,
    rebatesOnly,
    userType,
}: InvoiceLineItemDispensesTableListProps): JSX.Element => {
    const router = useRouter();
    const { fltStats, paging, cols, sections, sort, filters, setFilter, setSort, setPaging, waiting } =
        useInvoiceLineItemDispensesTableState();

    const { items } = useInvoiceLineItemDispensesData();

    const navToFeature = useCallback(
        (backing: InvoiceLineItemDispensesDataItem): void => {
            // nav to Mapping, Order Details or Invoice Details features
            // save context so user can return via breadcrumb link
            const routeData = {
                id: invoiceId,
                rxid: backing.RXID,
                lineitemselected: {
                    offsetTop: 0,
                    item: {
                        LineId: lineItemId,
                    },
                },
                rebatesOnly,
            };

            /**
             * NOTE:
             * This only works for CP Invoice, not Order Split.
             * Until we migrate Accumulation, navigating BACK to CP Invoice Details
             * will not work.
             */
            const breadcrumb = {
                icon: 'sprite-ndc-accum',
                route: 'invoice2.cpdetail',
                routeData: routeData,
                msg: 'Return to Invoice CP Detail',
            };

            const params = {
                Id: backing.AccumId,
                CE: backing.CEID,
                RX: backing.RXID,
                ShareGroup: null,
                CanEdit: userType == 'VERITYSA' || userType == 'VERITY' || userType == 'CE',
                breadcrumb: breadcrumb,
            };

            router.stateService.go('ops.accum-detail', params, { reload: 'ops.accum-detail' });
        },
        [invoiceId, lineItemId, rebatesOnly, router, userType],
    );

    const handleDispenseRowClick = useCallback(
        (rowItem: any): void => {
            navToFeature(rowItem);
        },
        [navToFeature],
    );

    if (waiting) {
        return <></>;
    }

    return (
        <TableList noSpacing>
            <TableTop
                id={invoiceLineItemDispensesTableId}
                fltStats={fltStats}
            />
            <table>
                <TableHead
                    cols={cols}
                    sections={sections}
                    sort={sort}
                    filters={filters}
                    setFilter={setFilter}
                    setSort={setSort}
                />
                <TableBody
                    cols={cols}
                    rows={items}
                    onClick={handleDispenseRowClick}
                />
            </table>
            <TablePaging
                paging={paging}
                setPaging={setPaging}
                fltStats={fltStats}
            />
        </TableList>
    );
};

export default InvoiceLineItemDispensesTableList;
